import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { CreateClaimTabInfo } from '../../../interfaces/create-claim-tab-info'
import { ClaimInformation } from 'app/interfaces/claim-information'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { SelectComponent } from '@components/select/select.component'
import { SelectValue } from '../../../interfaces/select-value.interface'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { RadioComponent } from '@components/radio/radio.component'
import {
    ClaimPlaceSelectValue,
    ClaimPlaceType,
    ClaimProductType,
    ClaimProductTypeSelectValue,
    ClaimType
} from '../../../interfaces/classification-types'
import { ApiService, UserInfo } from '../../../services/api.service'

@Component({
  selector: 'app-claim-classification',
  standalone: true,
    imports: [
        TranslateModule,
        SelectComponent,
        NgForOf,
        RadioComponent,
        NgIf,
        NgClass
    ],
  templateUrl: './claim-classification.component.html',
  styleUrl: './claim-classification.component.scss'
})
export class ClaimClassificationComponent implements OnInit, OnChanges {
    @Input() claimInformation!: ClaimInformation
    @Input() nextStepWarranty: boolean;
    @Input() userInfo!: UserInfo;
    @Output() tabInit = new EventEmitter<CreateClaimTabInfo>()
    @Output() updateClaim = new EventEmitter<ClaimInformation>()
    @Output() needWarranty = new EventEmitter<boolean>()
    @Output() onContinueState = new EventEmitter<boolean>()

    productTypeOptions: ClaimProductTypeSelectValue[] = [
        { id: <ClaimProductType>'slab', value: <ClaimProductType>'slab', label: this.translate.instant('create-claim.product-type.slab') },
        { id: <ClaimProductType>'standard', value: <ClaimProductType>'standard', label: this.translate.instant('create-claim.product-type.standard') },
        { id: <ClaimProductType>'cts', value: <ClaimProductType>'cts', label: this.translate.instant('create-claim.product-type.cts') },
        { id: <ClaimProductType>'professional', value: <ClaimProductType>'professional', label: this.translate.instant('create-claim.product-type.professional') },
    ]
    productType: ClaimProductTypeSelectValue | null = this.productTypeOptions[0]; // Default to 'slab'

    claimPlaceOptions: ClaimPlaceSelectValue[] = [
        { id: <ClaimPlaceType>'installation', value: <ClaimPlaceType>'installation', label: this.translate.instant('create-claim.claim-place.installation') },
        { id: <ClaimPlaceType>'in_shop', value: <ClaimPlaceType>'in_shop', label: this.translate.instant('create-claim.claim-place.in-shop') },
        { id: <ClaimPlaceType>'in_transit_center_to_shop', value: <ClaimPlaceType>'in_transit_center_to_shop', label: this.translate.instant('create-claim.claim-place.in-transit-center-to-shop') },
        { id: <ClaimPlaceType>'in_transit_hq_to_shop', value: <ClaimPlaceType>'in_transit_hq_to_shop', label: this.translate.instant('create-claim.claim-place.in-transit-hq-to-shop') },
        // { id: <ClaimPlaceType>'in_transit_shop_to_client', value: <ClaimPlaceType>'in_transit_shop_to_client', label: this.translate.instant('create-claim.claim-place.in-transit-shop-to-client') }
    ]
    claimPlace: ClaimPlaceSelectValue | null = null;

    claimTypeOptions: {
        value: ClaimType;
        selected: boolean;
        title: string;
        description?: string;
    }[] = [
        {
            value: 'visual',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.visual'),
            description: this.translate.instant('create-claim.claim-type.visual_description'),
        },
        {
            value: 'wrong_order',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.wrong_order'),
            description: this.translate.instant('create-claim.claim-type.wrong_order_description'),
        },
        {
            value: 'installation',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.installation'),
            description: this.translate.instant('create-claim.claim-type.installation_description'),
        },
        {
            value: 'superficial',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.superficial'),
            description: this.translate.instant('create-claim.claim-type.superficial_description'),
        },
        {
            value: 'impurities',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.impurities'),
            description: this.translate.instant('create-claim.claim-type.impurities_description'),
        },
        {
            value: 'marks',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.marks'),
            description: this.translate.instant('create-claim.claim-type.marks_description'),
        },
        {
            value: 'stains',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.stains'),
            description: this.translate.instant('create-claim.claim-type.stains_description'),
        },
        {
            value: 'organoleptic',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.organoleptic'),
            description: this.translate.instant('create-claim.claim-type.organoleptic_description'),
        },
        {
            value: 'broken',
            selected: false,
            title: this.translate.instant('create-claim.claim-type.broken'),
            description: this.translate.instant('create-claim.claim-type.broken_description'),
        }
    ]
    claimType: { value: ClaimType; selected: boolean; title: string; description?: string } | null = null;
    isClaimTypeDisabled: boolean = false;

    partnerStores: SelectValue[] = [];
    selectedPartnerStore: SelectValue | null = null;
    isMyOwnCustomer: boolean
    isPartnerStoreCustomer: boolean

    constructor(
        private translate: TranslateService,
        private api: ApiService
    ) {
    }

    ngOnInit() {
        this.tabInit.emit({
            currentTab: 'classification',
            previousTab: 'material-edit-area',
            nextTab: this.nextStepWarranty ? 'warranty' : 'documentation' // Warranty or documentation
        })

        this.claimInformation.lastModifiedTab = 'classification';

        if(!this.claimInformation.claimClassification.productType) {
            this.claimInformation.claimClassification.productType = 'slab'; // Default to 'slab' if nothing selected
        }

        this.api.getPartnerStores(this.claimInformation.claimInfo.id!).subscribe((partnerStores) => {
            for(let partnerStore of partnerStores) {
                if(partnerStore.id && partnerStore.name){
                    const selectValue = {
                        id: partnerStore.id,
                        value: partnerStore.id,
                        label: partnerStore.name
                    }
                    this.partnerStores.push(selectValue)
                    if(selectValue.id === this.claimInformation.claimClassification.accountId){
                        this.isPartnerStoreCustomer = true
                        this.selectedPartnerStore = selectValue
                    }
                }
            }
            if(this.claimInformation.claimClassification.claimPlace === 'installation' &&
                this.claimInformation.claimClassification.accountId === this.userInfo.salesforceAccountId) {
                this.isMyOwnCustomer = true
            }
            this.onContinueState.emit(this.canContinue());
        })

        this.initializeView()

        this.onContinueState.emit(this.canContinue());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['nextStepWarranty'] && !changes['nextStepWarranty'].firstChange) {
            this.tabInit.emit({
                currentTab: 'classification',
                previousTab: 'material-edit-area',
                nextTab: changes['nextStepWarranty'].currentValue ? 'warranty' : 'documentation' // Warranty or documentation
            })
        }
    }

    private canContinue() {
        const { claimClassification } = this.claimInformation
        // If a classification is missing, cannot continue
        if(!(claimClassification?.productType && claimClassification?.claimPlace && claimClassification?.claimType)){
            return false
        }

        if(claimClassification.claimPlace === 'installation'){
            // If claimPlace is installation and didn't select which customer, cannot continue
            if(!this.isMyOwnCustomer && !this.isPartnerStoreCustomer){
                return false;
            }

            // If selected partner store is the same as the users account then it is not selected, cannot continue
            if(this.isPartnerStoreCustomer && this.claimInformation.claimClassification.accountId === this.userInfo.salesforceAccountId){
                return false;
            }
        }

        return true;
    }

    onProductTypeSelected($event: SelectValue | null) {
        this.productType = <ClaimProductTypeSelectValue | null>$event
        if(this.productType) {
            this.claimInformation.claimClassification.productType = this.productType.value;
        }
        if(this.productType && this.productType.value === 'professional'){
            this.claimPlace = {
                id: <ClaimPlaceType>'in_shop',
                value: <ClaimPlaceType>'in_shop',
                label: this.translate.instant('create-claim.claim-place.in-shop')
            }
            this.claimInformation.claimClassification.claimPlace = this.claimPlace.value; // Set claim place to 'in_shop' for professional products
            this.isClaimTypeDisabled = true;
            this.needWarranty.emit(false)
        } else {
            this.claimPlace = null; // Reset claim place for non-professional products
            this.claimInformation.claimClassification.claimPlace = undefined;
            this.isClaimTypeDisabled = false;
        }

        this.updateClaimType()
    }

    onClaimPlaceSelected($event: SelectValue | null) {
        this.claimPlace = <ClaimPlaceSelectValue | null>$event

        if(this.claimPlace) {
            this.claimInformation.claimClassification.claimPlace = this.claimPlace.value;
            this.updateClaimType()
        }

        if(this.claimPlace && this.claimPlace.value === 'installation'){
            this.needWarranty.emit(true)
        } else {
            this.needWarranty.emit(false)
            this.claimInformation.warrantyData = {}
        }
    }

    onClaimTypeSelected(
        claimType: {
            value: ClaimType;
            selected: boolean;
            title: string;
            description?: string;
        } | null,
        resetDocumentation = false
    ) {
        this.claimTypeOptions.forEach(eachClaimType => {
            eachClaimType.selected = false; // Deselect all options
            if(claimType && eachClaimType.value === claimType.value) {
                eachClaimType.selected = true; // Select the clicked option
                this.claimType = claimType;
            }
        })

        this.claimInformation.claimClassification.claimType = this.claimType ? this.claimType.value : undefined; // Set selected claim type in claim information
        this.onClaimInfoChange(resetDocumentation); // Emit change event
    }

    shouldShowClaimType() {
        return this.claimPlace && (this.claimPlace.value === 'installation' || (this.claimPlace.value === 'in_shop' && this.productType && this.productType.value !== 'professional'))
    }

    private updateClaimType() {
        const claimProductType = this.claimInformation.claimClassification.productType;
        const claimPlace = this.claimInformation.claimClassification.claimPlace;
        switch (claimPlace) {
            case "in_shop":
                if(claimProductType === 'professional'){
                    this.claimInformation.claimClassification.claimType = 'wrong_order'; // Default to 'wrong_order' for in_shop
                }
                else{
                    this.resetClaimType()
                }
                break;
            case "in_transit_hq_to_shop":
            case "in_transit_center_to_shop":
            // case "in_transit_shop_to_client":
                this.claimInformation.claimClassification.claimType = 'transit';
                break;
            case "installation":
            case undefined:
                this.resetClaimType();
                break;
        }
        this.onClaimInfoChange(true);
    }

    private resetClaimType() {
        this.claimType = null; // Reset claim type
        this.claimTypeOptions.forEach(option => option.selected = false); // Deselect all options
        this.claimInformation.claimClassification.claimType = undefined; // Reset claim type in claim information
        this.isMyOwnCustomer = false
        this.isPartnerStoreCustomer = false
        this.selectedPartnerStore = null
        this.claimInformation.claimClassification.accountId = undefined
    }

    private onClaimInfoChange(resetDocumentation = false) {
        if(resetDocumentation){
            this.claimInformation.documentation = {
                cleanup: true,
                checks: [],
                documents: [],
                additionalInfo: []
            }
        }
        this.updateClaim.emit(this.claimInformation)
        this.onContinueState.emit(this.canContinue())
    }

    private initializeView() {
        this.productType = this.productTypeOptions.find(option => option.value === this.claimInformation.claimClassification.productType) || this.productTypeOptions[0];
        this.claimPlace = this.claimPlaceOptions.find(option => option.value === this.claimInformation.claimClassification.claimPlace) || null;
        this.claimType = this.claimTypeOptions.find(option => option.value === this.claimInformation.claimClassification.claimType) || null;
        this.onClaimTypeSelected(this.claimType)
    }

    onMyOwnCustomerSelected() {
        this.isMyOwnCustomer = true
        this.isPartnerStoreCustomer = false
        this.selectedPartnerStore = null
        this.claimInformation.claimClassification.accountId = this.userInfo.salesforceAccountId || undefined

        this.updateClaim.emit(this.claimInformation)
        this.onContinueState.emit(this.canContinue())
    }

    onPartnerStoreCustomerSelected() {
        this.isMyOwnCustomer = false
        this.isPartnerStoreCustomer = true

        this.onContinueState.emit(false)
    }

    onPartnerStoreSelected($event: SelectValue | null){
        this.selectedPartnerStore = $event;
        this.claimInformation.claimClassification.accountId = <string> $event!.id

        this.updateClaim.emit(this.claimInformation)
        this.onContinueState.emit(this.canContinue())
    }
}
